import type { GTooltipCardData } from '~/presentation/ui';

import { EMetricKey } from '~/types/metric';

type IMetricKeyValue = Record<EMetricKey, GTooltipCardData>;

export const ANALYTICS_METRIC_TOOLTIP: IMetricKeyValue = {
  [EMetricKey.SESSION]: {
    title: 'Sessions',
    content: 'A period during which a visitor interacts with your online store',
  },
  [EMetricKey.VISITORS]: {
    title: 'Visitors',
    content: 'Number of unique individuals who visit your online store',
  },
  [EMetricKey.BOUNCE_RATE]: {
    title: 'Bounce rate',
    content:
      'Percentage of visitors who leave your store after viewing only one page, without interacting further',
    formula: (
      <>
        <span className="formula-variable">Bounce rate</span>
        <span> = </span>
        <span className="formula-input">sessions with a pageview</span>
        <span className="formula-operator"> / </span>
        <span className="formula-input">total sessions</span>
      </>
    ),
  },
  [EMetricKey.CTR]: {
    title: 'Click-through rate',
    content:
      'Percentage of clicks to open new page, compared to the number of times the page was viewed',
    formula: (
      <>
        <span className="formula-variable">Click-through rate</span>
        <span> = </span>
        <span className="formula-input">total clicks</span>
        <span className="formula-operator"> / </span>
        <span className="formula-input">total pageviews</span>
      </>
    ),
  },
  [EMetricKey.AVG_TIME_ON_PAGE]: {
    title: 'Average time on page',
    content: 'Average duration that visitors spend on a page before clicking to open new page',
    formula: (
      <>
        <span className="formula-variable">Average time on page</span>
        <span> = </span>
        <span className="formula-input">total time on page</span>
        <span className="formula-operator"> / </span>
        <span className="formula-input">(total pageviews - total exits)</span>
      </>
    ),
  },
  [EMetricKey.PAGE_VIEWS]: {
    title: 'Pageviews',
    content: 'Number of times a page on your online store has been viewed by visitors',
  },
  [EMetricKey.RPV]: {
    title: 'Revenue per visitor',
    content: 'Average revenue generated from each visitor',
    formula: (
      <>
        <span className="formula-variable">Revenue per visitor</span>
        <span> = </span>
        <span className="formula-input">total revenue</span>
        <span className="formula-operator"> / </span>
        <span className="formula-input">total visitors</span>
      </>
    ),
  },
  [EMetricKey.ADDED_TO_CART]: {
    title: 'Added to cart',
    content:
      'Total number of the event when a customer adds a product to their cart on your online store',
  },
  [EMetricKey.REACHED_CHECKOUT]: {
    title: 'Sessions that reached checkout',
    content: 'Sessions in your online store in which the checkout page was reached',
  },
  [EMetricKey.COMPLETE_CHECKOUT]: {
    title: 'Sessions that completed checkout',
    content: 'Sessions in your online store in which a purchase was completed',
  },
  [EMetricKey.ORDERS]: {
    title: 'Orders',
    content: 'Number of orders that went through this pages',
  },
  [EMetricKey.CONVERSION_RATE]: {
    title: 'Conversion rate',
    content: 'Percentage of online store sessions that completed an order',
    formula: (
      <>
        <span className="formula-variable">Conversion rate</span>
        <span> = </span>
        <span className="formula-input">sessions that completed checkout</span>
        <span className="formula-operator"> / </span>
        <span className="formula-input">total sessions</span>
      </>
    ),
  },
  [EMetricKey.AOV]: {
    title: 'Average order value',
    content: 'Average value of orders placed in your online store',
    formula: (
      <>
        <span className="formula-variable">Average order value</span>
        <span> = </span>
        <span className="formula-input">total revenue</span>
        <span className="formula-operator"> / </span>
        <span className="formula-input">total orders</span>
      </>
    ),
  },
  [EMetricKey.REVENUE]: {
    title: 'Revenue',
    content: 'Total income generated from sales after discounts',
    formula: (
      <>
        <span className="formula-variable">Revenue</span>
        <span> = </span>
        <span className="formula-input">gross sales</span>
        <span className="formula-operator"> - </span>
        <span className="formula-input">discounts</span>
      </>
    ),
  },
  [EMetricKey.ADD_TO_CART_RATE]: {
    title: 'Add to cart rate',
    content: 'Percentage of sessions in which the customer added item to the cart',
    formula: (
      <>
        <span className="formula-variable">Added to cart rate</span>
        <span> = </span>
        <span className="formula-input">sessions with cart additions</span>
        <span className="formula-operator"> / </span>
        <span className="formula-input">total sessions</span>
      </>
    ),
  },
  [EMetricKey.CART_ADDITION]: {
    title: 'Sessions with cart additions',
    content: 'Sessions in your online store in which a visitor added item to the cart',
  },
};
